const INTERNAL_NAVIGATION_ORIGIN = 'https://internal.nynch.invalid';
const MAX_DECODE_PASSES = 8;
const AMBIGUOUS_LEADING_SEPARATOR_PATTERN = /^[\\/]{2}/;

function containsControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.charCodeAt(0);
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
}

function isAmbiguousPathText(pathname: string): boolean {
  return (
    !pathname.startsWith('/')
    || pathname.includes('\\')
    || containsControlCharacter(pathname)
    || AMBIGUOUS_LEADING_SEPARATOR_PATTERN.test(pathname)
  );
}

function hasUnsafePath(pathname: string): boolean {
  let decoded = pathname;

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    if (isAmbiguousPathText(decoded)) return true;

    // Dot-segment normalization can expose a leading double separator. Repeat
    // this canonicalization at every decode layer so nested encodings cannot
    // become dangerous in a later parser.
    try {
      const canonical = new URL(decoded, INTERNAL_NAVIGATION_ORIGIN);
      if (
        canonical.origin !== INTERNAL_NAVIGATION_ORIGIN
        || isAmbiguousPathText(canonical.pathname)
      ) {
        return true;
      }
    } catch {
      return true;
    }

    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return true;
    }

    if (next === decoded) return false;
    decoded = next;
  }

  // Fail closed when excessive encoding prevents the value from stabilizing.
  return true;
}

/**
 * Resolve an untrusted navigation target to an app-internal, root-relative path.
 *
 * Dynamic targets must not be handed directly to React Router. Browsers treat
 * leading slash/backslash combinations as protocol-relative URLs, and encoded
 * variants can become dangerous after a later decoding pass.
 */
export function resolveInternalNavigationPath(
  candidate: unknown,
): string | null {
  if (typeof candidate !== 'string') return null;

  const trimmed = candidate.trim();
  if (!trimmed || !trimmed.startsWith('/')) return null;

  const rawPathname = trimmed.split(/[?#]/, 1)[0];
  if (!rawPathname || hasUnsafePath(rawPathname)) return null;

  try {
    const parsed = new URL(trimmed, INTERNAL_NAVIGATION_ORIGIN);
    if (parsed.origin !== INTERNAL_NAVIGATION_ORIGIN) return null;
    if (hasUnsafePath(parsed.pathname)) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function buildOAuthCallbackUrl(location: { origin: string; search: string }): string {
  const callbackUrl = new URL('/auth', location.origin);
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const safeReturnTo = resolveInternalNavigationPath(returnTo);

  if (safeReturnTo) callbackUrl.searchParams.set('returnTo', safeReturnTo);
  return callbackUrl.toString();
}
